import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useImpersonation } from '@/contexts/ImpersonationContext';
import { Goal, Habit, EType, HabitLinkedAction, HabitFrequency } from '@/types/goal';
import { useToast } from '@/hooks/use-toast';
import { isHabitDueToday, getPreviousDueDate } from '@/lib/habit-schedule';
import { reportError } from '@/lib/error-reporting';
import { syncFoundingMemberStreak, syncFoundingMemberGoals } from '@/lib/founding-member-sync';
import { useProfile } from '@/hooks/useProfile';
import { getTierFromProfile } from '@/lib/tier-utils';
import { format } from 'date-fns';

export const useGoalsDB = () => {
  const [goals, setGoals] = useState<Goal[]>([]);
  const [completedGoals, setCompletedGoals] = useState<Goal[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { effectiveUserId, isImpersonating } = useImpersonation();
  const { toast } = useToast();
  const { profile } = useProfile();

  // Fetch goals and habits from database
  const fetchGoals = async () => {
    const userId = effectiveUserId || user?.id;
    if (!userId) {
      setGoals([]);
      setLoading(false);
      return;
    }

    try {
      // When impersonating, query by user_id explicitly
      // RLS allows admins to view data through impersonation context
      const { data: goalsData, error: goalsError } = await supabase
        .from('goals')
        .select('*')
        .eq('user_id', userId)
        .eq('archived', false)
        .order('created_at', { ascending: false });

      if (goalsError) throw goalsError;

      if (!goalsData || goalsData.length === 0) {
        setGoals([]);
        setLoading(false);
        return;
      }

      // Fetch habits for all goals
      const { data: habitsData, error: habitsError } = await supabase
        .from('habits')
        .select('*')
        .in('goal_id', goalsData.map(g => g.id))
        .eq('archived', false);

      if (habitsError) throw habitsError;

      // Fetch linked action details for habits that have linked_action_id
      const linkedActionIds = (habitsData || [])
        .filter(h => h.linked_action_id)
        .map(h => h.linked_action_id);

      const linkedActionsMap: Map<string, { milestoneIndex: number; actionIndex: number; planId: string }> = new Map();
      const milestonesMap: Map<string, { title: string; actions: string[] }[]> = new Map();

      if (linkedActionIds.length > 0) {
        // Fetch the milestone_actions records
        const { data: actionsData } = await supabase
          .from('milestone_actions')
          .select('id, plan_id, milestone_index, action_index')
          .in('id', linkedActionIds);

        if (actionsData) {
          actionsData.forEach(a => {
            linkedActionsMap.set(a.id, {
              milestoneIndex: a.milestone_index,
              actionIndex: a.action_index,
              planId: a.plan_id,
            });
          });

          // Fetch the development plans to get milestone names
          const planIds = [...new Set(actionsData.map(a => a.plan_id))];
          const { data: plansData } = await supabase
            .from('development_plans')
            .select('id, milestones')
            .in('id', planIds);

          if (plansData) {
            plansData.forEach(p => {
              milestonesMap.set(p.id, (p.milestones as unknown as { title: string; actions: string[] }[]) || []);
            });
          }
        }
      }

      // Helper to get linked action info
      const getLinkedAction = (linkedActionId: string | null): HabitLinkedAction | undefined => {
        if (!linkedActionId) return undefined;
        const actionInfo = linkedActionsMap.get(linkedActionId);
        if (!actionInfo) return undefined;
        
        const milestones = milestonesMap.get(actionInfo.planId);
        if (!milestones || !milestones[actionInfo.milestoneIndex]) return undefined;
        
        const milestone = milestones[actionInfo.milestoneIndex];
        return {
          actionId: linkedActionId,
          milestoneIndex: actionInfo.milestoneIndex,
          actionIndex: actionInfo.actionIndex,
          milestoneName: milestone.title,
          actionText: milestone.actions[actionInfo.actionIndex] || '',
        };
      };

      // Map to Goal type
      const mappedGoals: Goal[] = goalsData.map(g => ({
        id: g.id,
        title: g.title,
        description: g.description || '',
        eTypes: (g.e_types || []) as EType[],
        progress: g.progress,
        createdAt: new Date(g.created_at),
        targetDate: g.target_date ? new Date(g.target_date) : undefined,
        milestones: [],
        habits: (habitsData || [])
          .filter(h => h.goal_id === g.id)
          .map(h => ({
            id: h.id,
            title: h.title,
            streak: h.streak,
            completedDates: h.completed_dates || [],
            frequency: (h.frequency || 'daily') as HabitFrequency,
            scheduleDays: h.schedule_days ?? null,
            createdAt: h.created_at,
            linkedAction: getLinkedAction(h.linked_action_id),
          })),
      }));

      setGoals(mappedGoals);
    } catch (error: any) {
      reportError(error, 'goals', { action: 'fetch-goals' });
      toast({
        title: 'Error loading goals',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCompletedGoals = async () => {
    const userId = effectiveUserId || user?.id;
    if (!userId) return;

    try {
      const { data, error } = await supabase
        .from('goals')
        .select('*')
        .eq('user_id', userId)
        .eq('archived', true)
        .order('updated_at', { ascending: false });

      if (error) {
        reportError(error, 'goals', { action: 'fetch-completed-goals' });
        return;
      }

      setCompletedGoals((data || []).map(g => ({
        id: g.id,
        title: g.title,
        description: g.description || '',
        eTypes: (g.e_types || []) as EType[],
        progress: g.progress,
        createdAt: new Date(g.created_at),
        targetDate: g.target_date ? new Date(g.target_date) : undefined,
        milestones: [],
        habits: [],
      })));
    } catch (error) {
      reportError(error, 'goals', { action: 'fetch-completed-goals' });
    }
  };

  useEffect(() => {
    fetchGoals();
    fetchCompletedGoals();
  }, [user?.id, effectiveUserId]);

  const addGoal = async (title: string, description: string, eTypes: EType[], targetDate?: Date) => {
    if (!user) return null;

    try {
      const { data, error } = await supabase
        .from('goals')
        .insert({
          user_id: user.id,
          title,
          description,
          e_types: eTypes,
          target_date: targetDate?.toISOString() || null,
        })
        .select()
        .single();

      if (error) throw error;

      const newGoal: Goal = {
        id: data.id,
        title: data.title,
        description: data.description || '',
        eTypes: (data.e_types || []) as EType[],
        progress: data.progress,
        createdAt: new Date(data.created_at),
        targetDate: data.target_date ? new Date(data.target_date) : undefined,
        milestones: [],
        habits: [],
      };

      setGoals(prev => [newGoal, ...prev]);
      
      // Track goal creation (fire-and-forget)
      import('@/lib/analytics').then(({ trackGoalCreated }) => {
        trackGoalCreated(getTierFromProfile(profile) || 'free', goals.length + 1);
      }).catch(() => {});

      // Emit engagement event (fire-and-forget)
      import('@/lib/engagement-events').then(({ emitEngagementEvent }) => {
        emitEngagementEvent('goal_created', { goalId: data.id });
      }).catch(() => {});

      return newGoal;
    } catch (error: any) {
      reportError(error, 'goals', { action: 'create-goal' });
      toast({
        title: 'Error creating goal',
        description: error.message,
        variant: 'destructive',
      });
      return null;
    }
  };

  const updateGoal = async (id: string, updates: Partial<Goal>) => {
    try {
      const dbUpdates: any = {};
      if (updates.title !== undefined) dbUpdates.title = updates.title;
      if (updates.description !== undefined) dbUpdates.description = updates.description;
      if (updates.eTypes !== undefined) dbUpdates.e_types = updates.eTypes;
      if (updates.progress !== undefined) dbUpdates.progress = updates.progress;
      if (updates.targetDate !== undefined) dbUpdates.target_date = updates.targetDate?.toISOString();

      const { error } = await supabase
        .from('goals')
        .update(dbUpdates)
        .eq('id', id);

      if (error) throw error;

      setGoals(prev =>
        prev.map(goal => (goal.id === id ? { ...goal, ...updates } : goal))
      );
    } catch (error: any) {
      reportError(error, 'goals', { action: 'update-goal' });
      toast({
        title: 'Error updating goal',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const deleteGoal = async (id: string) => {
    try {
      const { error } = await supabase
        .from('goals')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setGoals(prev => prev.filter(goal => goal.id !== id));
    } catch (error: any) {
      reportError(error, 'goals', { action: 'delete-goal' });
      toast({
        title: 'Error deleting goal',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const completeGoal = async (id: string): Promise<boolean> => {
    const userId = effectiveUserId || user?.id;
    if (!userId) return false;

    try {
      const { error } = await supabase
        .from('goals')
        .update({ archived: true, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;

      // Remove from active goals
      setGoals(prev => prev.filter(g => g.id !== id));

      // Sync founding member progress (fire-and-forget)
      syncFoundingMemberGoals(userId);

      // Emit engagement event (fire-and-forget)
      import('@/lib/engagement-events').then(({ emitEngagementEvent }) => {
        emitEngagementEvent('goal_completed', { goalId: id });
      }).catch(() => {});

      // Refresh completed goals list
      fetchCompletedGoals();

      return true;
    } catch (error: any) {
      reportError(error, 'goals', { action: 'complete-goal' });
      toast({
        title: 'Error completing goal',
        description: error.message,
        variant: 'destructive',
      });
      return false;
    }
  };

  const addHabit = async (goalId: string, title: string, frequency: HabitFrequency = 'daily', scheduleDays: number | null = 127, linkedActionId?: string) => {
    try {
      const insertData: {
        goal_id: string;
        title: string;
        frequency: string;
        schedule_days?: number | null;
        linked_action_id?: string;
      } = {
        goal_id: goalId,
        title,
        frequency,
        schedule_days: scheduleDays,
      };

      if (linkedActionId) {
        insertData.linked_action_id = linkedActionId;
      }

      const { data, error } = await supabase
        .from('habits')
        .insert(insertData)
        .select()
        .single();

      if (error) throw error;

      const newHabit: Habit = {
        id: data.id,
        title: data.title,
        streak: data.streak,
        completedDates: data.completed_dates || [],
        frequency: (data.frequency || 'daily') as HabitFrequency,
        scheduleDays: data.schedule_days ?? null,
        createdAt: data.created_at,
      };

      setGoals(prev =>
        prev.map(goal =>
          goal.id === goalId
            ? { ...goal, habits: [...goal.habits, newHabit] }
            : goal
        )
      );
      
      // Track habit creation (fire-and-forget)
      if (user) {
        import('@/lib/analytics').then(({ trackHabitCreated }) => {
          const totalHabits = goals.reduce((sum, g) => sum + g.habits.length, 0) + 1;
          trackHabitCreated(getTierFromProfile(profile) || 'free', totalHabits);
        }).catch(() => {});
      }

      // Emit engagement event (fire-and-forget)
      import('@/lib/engagement-events').then(({ emitEngagementEvent }) => {
        emitEngagementEvent('habit_added', { habitId: data.id, goalId });
      }).catch(() => {});
    } catch (error: any) {
      reportError(error, 'goals', { action: 'add-habit' });
      toast({
        title: 'Error adding habit',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const toggleHabitForToday = async (goalId: string, habitId: string): Promise<{
    milestoneReached?: number;
    habitTitle?: string;
    newStreak?: number;
  } | void> => {
    const today = format(new Date(), 'yyyy-MM-dd');
    
    const goal = goals.find(g => g.id === goalId);
    const habit = goal?.habits.find(h => h.id === habitId);
    if (!habit) return;

    const isCompleted = habit.completedDates.includes(today);
    let newCompletedDates: string[];
    let newStreak: number;
    const previousStreak = habit.streak;

    if (isCompleted) {
      newCompletedDates = habit.completedDates.filter(d => d !== today);
      newStreak = Math.max(0, habit.streak - 1);
    } else {
      newCompletedDates = [...habit.completedDates, today];

      if (habit.frequency === 'weekly') {
        // For weekly habits, check if completed in the previous week
        const now = new Date();
        const dayOfWeek = now.getDay();
        const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        const thisWeekStart = new Date(now);
        thisWeekStart.setDate(thisWeekStart.getDate() + mondayOffset);
        thisWeekStart.setHours(0, 0, 0, 0);
        const lastWeekStart = new Date(thisWeekStart);
        lastWeekStart.setDate(lastWeekStart.getDate() - 7);
        const lastWeekEnd = new Date(thisWeekStart);
        lastWeekEnd.setDate(lastWeekEnd.getDate() - 1);

        const completedLastWeek = habit.completedDates.some(d => {
          const date = new Date(d + 'T00:00:00');
          return date >= lastWeekStart && date <= lastWeekEnd;
        });

        if (completedLastWeek || habit.streak === 0) {
          newStreak = habit.streak + 1;
        } else {
          newStreak = 1;
        }
      } else {
        // For all other frequencies, use getPreviousDueDate for streak calc
        const prevDue = getPreviousDueDate(habit.frequency, habit.scheduleDays, habit.createdAt);
        const prevDueStr = prevDue ? format(prevDue, 'yyyy-MM-dd') : null;

        if ((prevDueStr && habit.completedDates.includes(prevDueStr)) || habit.streak === 0) {
          newStreak = habit.streak + 1;
        } else {
          newStreak = 1;
        }
      }
    }

    // Optimistic update — apply immediately to prevent UI flicker
    // Deep copy for rollback — a shallow reference would point to the modified state
    const previousGoals = goals.map(g => ({
      ...g,
      habits: g.habits.map(h => ({ ...h, completedDates: [...h.completedDates] })),
    }));
    setGoals(prev =>
      prev.map(g => {
        if (g.id !== goalId) return g;
        return {
          ...g,
          habits: g.habits.map(h => {
            if (h.id !== habitId) return h;
            return {
              ...h,
              completedDates: newCompletedDates,
              streak: newStreak,
            };
          }),
        };
      })
    );

    try {
      const { error } = await supabase
        .from('habits')
        .update({
          completed_dates: newCompletedDates,
          streak: newStreak,
        })
        .eq('id', habitId);

      if (error) throw error;

      // Sync streak to founding member progress (fire-and-forget)
      const userId = effectiveUserId || user?.id;
      if (userId) {
        syncFoundingMemberStreak(userId, newStreak);
      }

      // Check for milestone achievements (7, 30, 90 days)
      const milestones = [7, 30, 90];
      const reachedMilestone = milestones.find(m =>
        newStreak === m && previousStreak < m
      );

      if (reachedMilestone) {
        // Emit streak milestone engagement event (fire-and-forget)
        import('@/lib/engagement-events').then(({ emitEngagementEvent }) => {
          emitEngagementEvent('streak_milestone', { days: reachedMilestone, habitTitle: habit.title });
        }).catch(() => {});

        return {
          milestoneReached: reachedMilestone,
          habitTitle: habit.title,
          newStreak,
        };
      }

      // Detect streak break (was >=3, now reset to 1)
      if (!isCompleted && newStreak === 1 && previousStreak >= 3) {
        import('@/lib/engagement-events').then(({ emitEngagementEvent }) => {
          emitEngagementEvent('streak_broken', { previousStreak, habitTitle: habit.title });
        }).catch(() => {});
      }

      return { newStreak };
    } catch (error: any) {
      // Rollback optimistic update on error
      setGoals(previousGoals);
      reportError(error, 'goals', { action: 'toggle-habit' });
      toast({
        title: 'Error updating habit',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const getTotalStreak = () => {
    return goals.reduce((total, goal) => {
      return total + goal.habits.reduce((sum, habit) => sum + habit.streak, 0);
    }, 0);
  };

  const getTodayProgress = () => {
    const today = format(new Date(), 'yyyy-MM-dd');
    const now = new Date();
    let totalHabits = 0;
    let completedToday = 0;

    goals.forEach(goal => {
      goal.habits.forEach(habit => {
        if (!isHabitDueToday(habit.frequency, habit.scheduleDays, habit.createdAt, now)) return;
        totalHabits++;
        if (habit.completedDates.includes(today)) {
          completedToday++;
        }
      });
    });

    return totalHabits > 0 ? Math.round((completedToday / totalHabits) * 100) : 0;
  };

  return {
    goals,
    completedGoals,
    loading,
    addGoal,
    updateGoal,
    deleteGoal,
    completeGoal,
    addHabit,
    toggleHabitForToday,
    getTotalStreak,
    getTodayProgress,
    refetch: fetchGoals,
  };
};
